import * as React from "react";
import {JSX, useCallback, useEffect, useState} from "react";
import Box from "@mui/material/Box";
import clsx from "clsx";
import css from "../Toolbar/Toolbar.module.css";
import {Collapse, IconButton, Input, Popover} from "@mui/material";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {SearchInput} from "../Toolbar/SearchInput.tsx";
import {GoodFilterBuilder, GoodFilterInterface} from "../Model/GoodFilter.ts";
import {TwoLineBtn} from "../../Components/Button/TwoLineBtn.tsx";
import {DateRange} from "../../Components/Date/DateRange.tsx";
import dayjs, {Dayjs} from "dayjs";
import {DEF} from "../../const.ts";
import Profile from "../Model/Profile.ts";
import GoodStatus from "../Model/GoodStatus.ts";
import GoodApi from "./GoodApi.ts";

export interface GoodToolbarProps {
    token: string
    profile: Profile,
    filter: GoodFilterInterface,
    setFilter: React.Dispatch<React.SetStateAction<GoodFilterInterface>>,
    filterReady: boolean,
    setFilterReady: React.Dispatch<React.SetStateAction<boolean>>,
    menuIx:number
}

const GoodMapToolbar: React.FC<GoodToolbarProps> = (props) : JSX.Element => {
    const [goodStatus, setGoodStatus] = React.useState<GoodStatus>();

    const [popoverContent, setPopoverContent] = React.useState<JSX.Element | null>(<></>);
    const [popoverAnchorEl, setPopoverAnchorEl] = React.useState<HTMLButtonElement | null>(null);
    //
    const [popoverClose, setPopoverClose] = React.useState<() => void>((): () => void => () : void => {});
    const [canClosePopover, setCanClosePopover] = React.useState<boolean>(true);

    //-----------------------------------------------------------
    // Główne inicjacje
    useEffect(() : void => {
        const fetchStatuses = async () : Promise<void> => {
            const api = new GoodApi(props.token, props.profile);
            try {
                const goodStatuses = await api.goodStatusTypes();
                setGoodStatus({...goodStatuses});
            } catch (e) {
                console.error(e);
            }
        }

        fetchStatuses();
    }, []);
    //-----------------------------------------------------------

    //-----------------------------------------------------------
    // epid
    const [epid, setEpid] = React.useState<string>('');
    const epidInputDraw = () : JSX.Element => {
        return (
            <>
                <SearchInput className={clsx(css.inputText, css.inputEpid, css.border)}
                             placeholder="EPID"
                             value={epid}
                             setValue={setEpid}/>
            </>
        )
    }
    useEffect(() => {
        props.filter.epid = epid ?? null;
        props.setFilterReady(true);
    }, [epid]);
    // epid
    //-----------------------------------------------------------

    //-----------------------------------------------------------
    // dobro
    const [description, setDescription] = React.useState<string>('');
    const descriptionInputDraw = () : JSX.Element => {
        return (
            <>
                <SearchInput className={clsx(css.inputText, css.inputDescription, css.border)}
                             placeholder="Opis dobra"
                             value={description}
                             setValue={setDescription}
                />
            </>
        )
    }
    useEffect(() : void => {
        if (description.length > 0) {
            props.filter.description = description;
        } else {
            props.filter.description = null;
        }
    }, [description]);
    // description
    //-----------------------------------------------------------

    //-----------------------------------------------------------
    // dateCreate
    const [dateFrom, setDateFrom] = React.useState<Dayjs>(
        props.filter.dateFrom
        ?? dayjs().subtract(3, 'month').startOf('month')
    );
    const [dateTo, setDateTo] = React.useState<Dayjs>(
        props.filter.dateTo
        ?? dayjs().subtract(1, 'day').endOf('day')
    );
    const [dateTopRow, setDateTopRow] = React.useState<React.JSX.Element>(<></>);
    const [dateSuccess, setDateSuccess] = React.useState<boolean>(false);

    const dateCreateBtnDraw = () : JSX.Element => {
        return (
            <>
                <TwoLineBtn className={clsx(css.creationDate)}
                            topRow={dateTopRow}
                            bottomRow={'Data'}
                            onClick={dateCreateBtnClick}/>
            </>
        )
    }

    const dateCreateBtnClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
        setPopoverAnchorEl(event.currentTarget);
        setCanClosePopover(true);

        setPopoverContent((): JSX.Element => {
            return (
                <>
                    <DateRange dateFrom={dateFrom}
                               setDateFrom={setDateFrom}
                               dateTo={dateTo}
                               setDateTo={setDateTo}
                               setStatus={setDateSuccess}
                    />
                </>
            )
        })
    }

    useEffect(() : void => {
        const dateCreateTopRowContent = ((): JSX.Element => {
            return (
                <Box sx={{display: 'flex', flexDirection: 'column', lineHeight: 1.0, fontSize:"14px"}}>
                    <span>Od: {dateFrom.format(DEF.DATE_FORMAT)}</span>
                    <span>Do: {dateTo.format(DEF.DATE_FORMAT)}</span>
                </Box>
            );
        })();

        setDateTopRow(dateCreateTopRowContent);
    }, [dateFrom, dateTo]);

    useEffect(() : void => {
        setCanClosePopover(dateSuccess)
    }, [dateSuccess]);
    // dateCreate
    //---------------------------------------------------------

    //-----------------------------------------------------------
    // goodAddress
    const [goodAddress, setGoodAddress] = React.useState<string>('');
    const goodAddressDraw = () : JSX.Element => {
        return (
            <>
                <SearchInput className={clsx(css.textInput, css.border)}
                             placeholder="Loklizacja"
                             value={goodAddress}
                             setValue={setGoodAddress}
                />
            </>
        )
    }

    useEffect(():void => {
        if (goodAddress.length > 0) {
            props.filter.goodAddress = goodAddress;
        } else {
            props.filter.goodAddress = null;
        }
    }, [goodAddress]);
    // installationAddress
    //-----------------------------------------------------------

    //-----------------------------------------------------------
    // Punkty
    const [pointsFrom, setPointsFrom] = React.useState<number|null>(null);
    const [pointsTo, setPointsTo] = React.useState<number|null>(null);

    const pointsInputDraw = (): JSX.Element => {

        return (
            <Box className={clsx(css.numberRange, css.border)}
                 sx={{
                     display: 'flex',
                     flexDirection: 'row',
                     alignItems: 'center',
                     gap: 3}}
            >
                Punkty:
                <span>Od</span>

                <Input

                    type="number"
                    value={pointsFrom || ''}
                    onChange={(e):void => setPointsFrom(parseInt(e.target.value) || 0)}
                    sx={{width: '5ch'}}
                    inputProps={{min: 0, max: 11000}}
                />
                <span>Do</span>
                <Input
                    type="number"
                    value={pointsTo || ''}
                    onChange={(e):void => setPointsTo(parseInt(e.target.value) || 0)}
                    sx={{width: '5ch'}}
                    inputProps={{min: 0, max: 11000}}
                />
            </Box>
        )
    };

    useEffect(():void => {
        if (!pointsFrom) {
            props.filter.pointsFrom = null;
            setPointsFrom(null);
        } else {
            props.filter.pointsFrom = pointsFrom;
            if (pointsTo && pointsFrom && pointsFrom > pointsTo) {
                setPointsTo(pointsFrom);
                props.filter.pointsTo = pointsFrom;
            }
        }
    }, [pointsFrom]);

    useEffect(():void => {
        if (!pointsTo) {
            props.filter.pointsTo = null;
            props.filter.pointsFrom = null;
            setPointsTo(null);
            setPointsFrom(null);
        } else {
            props.filter.pointsTo = pointsTo;
            if (pointsFrom && pointsTo && pointsTo < pointsFrom) {
                props.filter.pointsFrom = pointsTo;
                setPointsFrom(pointsTo);
            }
        }
    }, [pointsTo]);
    // Punkty
    //-----------------------------------------------------------

    //-----------------------------------------------------------
    // status
    const [status, setStatus] = React.useState<string|null>(null);
    const goodStatusDraw = (): JSX.Element => {
        return (
            <Box sx={{minWidth: 200}}>
                <select className={clsx(css.select, css.border)}
                    aria-label="Status dobra"
                    value={status||''}
                    onChange={(e) : void => {setStatus(e.target.value)}}
                >
                    <option value=''>Wybierz status</option>
                    {goodStatus && Object.entries(goodStatus).map(([key, value]) : JSX.Element => (
                        <option key={value} value={key}>
                            {value}
                        </option>
                    ))}
                </select>
            </Box>
        );
    };
    // status
    //-----------------------------------------------------------


    //-----------------------------------------------------------
    // Obsługa filtra
    useEffect(() : void => {
        const isDataValid = () : boolean => {
            return true;
        };

        if (isDataValid()) {
            let builder = new GoodFilterBuilder();
            builder.description(description)
                    .dateFrom(dateFrom)
                    .dateTo(dateTo)
                    .goodAddress(goodAddress)
                    .pointsFrom(pointsFrom)
                    .pointsTo(pointsTo)
                    .goodStage(status);

            props.setFilter(builder.build());
            props.setFilterReady(true);
        } else {
            props.setFilterReady(false);
        }
    }, [description, dateFrom, dateTo, goodAddress, pointsFrom, pointsTo, status]);

    // Obsługa filtra
    //-----------------------------------------------------------


    // @ts-ignore
    const handleClose = useCallback((_event, _reason):void => {

        if (!canClosePopover) {
            return;
        }

        popoverClose();
        setPopoverClose(() : () => void => () : void => {});

        setPopoverAnchorEl(null);
        setPopoverContent(null);
    }, [canClosePopover]);


    const open = Boolean(popoverAnchorEl)
    const [expanded, setExpanded] = useState(false);

    const togglePanel = () : void => setExpanded(!expanded);

    const buttonsOnToolbar = () : JSX.Element => {
        return (
            <Box className={clsx(css.controls)}>

                <IconButton onClick={togglePanel} color="primary" size="small">
                    {expanded ? <ExpandLessIcon fontSize="small"/> : <ExpandMoreIcon fontSize="small"/>}
                </IconButton>

                <Box className={clsx(css.main)}>

                    {epidInputDraw()}

                    {descriptionInputDraw()}

                    {dateCreateBtnDraw()}

                    {goodAddressDraw()}

                    {pointsInputDraw()}

                    {goodStatusDraw()}

                </Box>

                <IconButton onClick={togglePanel} color="primary" size="small">
                    {expanded ? <ExpandLessIcon fontSize="small"/> : <ExpandMoreIcon fontSize="small"/>}
                </IconButton>
            </Box>
        )
    }

    const collapse = (): React.JSX.Element => {
        return (

            <Box className={clsx(css.collapse)}>
                <Collapse in={expanded} timeout="auto" unmountOnExit>
                    <Box className={clsx(css.content)}>
                        <Box>Dodatkowe filtry daty...</Box>
                        <Box>Wybór kategorii...</Box>
                        <Box>Opcje zaawansowane...</Box>
                    </Box>
                </Collapse>
            </Box>

        )
    }

    const popover = (): React.JSX.Element => {
        return (

            <Popover className={clsx(css.popover)}
                open={open}
                anchorEl={popoverAnchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                slotProps={{
                    paper: {
                        className: clsx(css.leadToolbar_popover_slot)
                    }
                }}
            >

                <Box  className={clsx(css.content)}>
                    {popoverContent}
                </Box>
            </Popover>

        )
    }

    return (

        <Box className={clsx(css.toolBar)}>

            {buttonsOnToolbar()}
            
            {collapse()}
            
            {popover()}
        </Box>
    )
}

export default GoodMapToolbar;